import { useMemo } from "react";

import type { ContextField } from "@/app/_services/AutomationService";

type Group = { key: string; label: string; fields: { path: string; label: string }[] };

/**
 * The variables an automation can actually render, grouped by namespace.
 *
 * Only the pick behaviour differs between editors -- the block builder has no reachable
 * caret so it copies, the SMS textarea inserts.
 */
export default function VariableList({
  contextFields,
  legacyFields = [],
  onPick,
}: {
  contextFields: ContextField[];
  legacyFields?: { path: string; label: string }[];
  onPick: (path: string) => void;
}) {
  const groups = useMemo<Group[]>(() => {
    const byGroup = new Map<string, Group>();
    for (const field of contextFields) {
      if (!byGroup.has(field.group)) {
        byGroup.set(field.group, { key: field.group, label: field.group, fields: [] });
      }
      byGroup.get(field.group)!.fields.push({ path: field.path, label: field.label });
    }
    const out = Array.from(byGroup.values());
    if (legacyFields.length > 0) {
      out.push({ key: "__legacy__", label: "Legacy names", fields: legacyFields });
    }
    return out;
  }, [contextFields, legacyFields]);

  if (groups.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      {groups.map((group) => (
        <div key={group.key} className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
            {group.label}
          </span>
          <div className="flex flex-wrap gap-1.5">
            {group.fields.map((field) => (
              <button
                key={`${group.key}:${field.path}`}
                type="button"
                title={field.label}
                onClick={() => onPick(field.path)}
                className="rounded border border-border bg-card px-1.5 py-0.5 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <code>{`{{${field.path}}}`}</code>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
